// src/components/LogoutSummary.tsx
import React, { useState, useEffect } from 'react';
import { Activity, FileText, UserCheck, Clock, ClipboardList, Mic } from 'lucide-react';
import './LogoutSummary.css';

interface LogoutSummaryProps {
  userName: string;
  activitiesLogged: number;
  handoffsSubmitted: number;
  patientsCaresFor: number;
  sessionDuration: string;
  reportsSubmitted: number;
  otherRecordingsSubmitted: number;
  onContinueToLogin: () => void;
  isAdmin?: boolean;
  isNurseAdminDashboard?: boolean;
  isSessionTimeout?: boolean;
}

const LogoutSummary: React.FC<LogoutSummaryProps> = ({
  userName,
  activitiesLogged,
  handoffsSubmitted,
  patientsCaresFor,
  sessionDuration,
  reportsSubmitted,
  otherRecordingsSubmitted,
  onContinueToLogin,
  isAdmin = false,
  isNurseAdminDashboard = false,
  isSessionTimeout = false
}) => {
  const [fadeOut, setFadeOut] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [countedActivities, setCountedActivities] = useState(0);
  const [countedHandoffs, setCountedHandoffs] = useState(0);
  const [countedPatients, setCountedPatients] = useState(0);
  const [countedReports, setCountedReports] = useState(0);
  const [countedRecordings, setCountedRecordings] = useState(0);

  // Trigger stats animation after initial fade-in
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowStats(true);
    }, 600);
    return () => clearTimeout(timer);
  }, []);

  // Animate counting up
  useEffect(() => {
    if (!showStats) return;

    const duration = 1500; // 1.5 seconds for count animation
    const steps = 30;
    const interval = duration / steps;

    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      const easeOut = 1 - Math.pow(1 - progress, 3); // Cubic ease-out

      setCountedActivities(Math.round(activitiesLogged * easeOut));
      setCountedHandoffs(Math.round(handoffsSubmitted * easeOut));
      setCountedPatients(Math.round(patientsCaresFor * easeOut));
      setCountedReports(Math.round(reportsSubmitted * easeOut));
      setCountedRecordings(Math.round(otherRecordingsSubmitted * easeOut));

      if (step >= steps) {
        clearInterval(timer);
        setCountedActivities(activitiesLogged);
        setCountedHandoffs(handoffsSubmitted);
        setCountedPatients(patientsCaresFor);
        setCountedReports(reportsSubmitted);
        setCountedRecordings(otherRecordingsSubmitted);
      }
    }, interval);

    return () => clearInterval(timer);
  }, [showStats, activitiesLogged, handoffsSubmitted, patientsCaresFor, reportsSubmitted, otherRecordingsSubmitted]);

  const handleContinue = () => {
    setFadeOut(true);
    setTimeout(() => {
      onContinueToLogin();
    }, 800);
  };

  // Auto-continue after 8 seconds (or 1.5 seconds for admin/nurse admin dashboard/session timeout)
  useEffect(() => {
    const timer = setTimeout(() => {
      handleContinue();
    }, (isAdmin || isNurseAdminDashboard || isSessionTimeout) ? 1500 : 8000);
    return () => clearTimeout(timer);
  }, [isAdmin, isNurseAdminDashboard, isSessionTimeout]);

  // Admin, nurse admin dashboard, or session timeout logout screen - simple redirect message
  if (isAdmin || isNurseAdminDashboard || isSessionTimeout) {
    return (
      <div className={`logout-summary ${fadeOut ? 'fade-out' : ''}`}>
        <div className="logout-background-dots" />
        <div className="logout-content">
          <div className="logout-logo-section">
            <div className="logout-brand-row">
              <img src="/logo.png" alt="CareShift Logo" className="logout-logo-image" />
              <h2 className="logout-brand-title">CareShift</h2>
            </div>
          </div>
          <div className="logout-text-section">
            <h1 className="logout-title">Logging out...</h1>
            <p className="logout-subtitle">Redirecting to login screen</p>
          </div>
          <div className="logout-loading-spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className={`logout-summary ${fadeOut ? 'fade-out' : ''}`}>
      <div className="logout-background-dots" />
      
      <div className="logout-content">
        <div className="logout-logo-section">
          <div className="logout-brand-row">
            <img src="/logo.png" alt="CareShift Logo" className="logout-logo-image" />
            <h2 className="logout-brand-title">CareShift</h2>
          </div>
        </div>

        <div className="logout-text-section">
          <h1 className="logout-title">Great work, {userName}!</h1>
          <p className="logout-subtitle">Here's a summary of what you accomplished:</p>
        </div>

        <div className={`logout-stats-grid ${showStats ? 'show' : ''}`}>
          <div className="logout-stat-card" style={{ animationDelay: '0.1s' }}>
            <div className="logout-stat-icon logout-stat-icon-activity">
              <Activity size={28} />
            </div>
            <div className="logout-stat-info">
              <span className="logout-stat-value">{countedActivities}</span>
              <span className="logout-stat-label">Activities Logged</span>
            </div>
          </div>

          <div className="logout-stat-card" style={{ animationDelay: '0.2s' }}>
            <div className="logout-stat-icon logout-stat-icon-handoff">
              <FileText size={28} />
            </div>
            <div className="logout-stat-info">
              <span className="logout-stat-value">{countedHandoffs}</span>
              <span className="logout-stat-label">Handoffs Submitted</span>
            </div>
          </div>

          <div className="logout-stat-card" style={{ animationDelay: '0.3s' }}>
            <div className="logout-stat-icon logout-stat-icon-patients">
              <UserCheck size={28} />
            </div>
            <div className="logout-stat-info">
              <span className="logout-stat-value">{countedPatients}</span>
              <span className="logout-stat-label">Patients Cared For</span>
            </div>
          </div>

          <div className="logout-stat-card" style={{ animationDelay: '0.4s' }}>
            <div className="logout-stat-icon logout-stat-icon-reports">
              <ClipboardList size={28} />
            </div>
            <div className="logout-stat-info">
              <span className="logout-stat-value">{countedReports}</span>
              <span className="logout-stat-label">Reports Submitted</span>
            </div>
          </div>

          <div className="logout-stat-card" style={{ animationDelay: '0.5s' }}>
            <div className="logout-stat-icon logout-stat-icon-recordings">
              <Mic size={28} />
            </div>
            <div className="logout-stat-info">
              <span className="logout-stat-value">{countedRecordings}</span>
              <span className="logout-stat-label">Other Interactions</span>
            </div>
          </div>

          <div className="logout-stat-card" style={{ animationDelay: '0.6s' }}>
            <div className="logout-stat-icon logout-stat-icon-time">
              <Clock size={28} />
            </div>
            <div className="logout-stat-info">
              <span className="logout-stat-value">{sessionDuration}</span>
              <span className="logout-stat-label">Session Duration</span>
            </div>
          </div>
        </div>

        <button 
          className="logout-continue-button"
          onClick={handleContinue}
        >
          <span className="button-text">Continue to Login</span>
          <svg className="button-arrow" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
          </svg>
        </button>

        <p className="logout-auto-redirect">Redirecting automatically in a few seconds...</p>
      </div>
    </div>
  );
};

export default LogoutSummary;
